import React, { useContext, useEffect, useState } from "react";

import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Phone as PhoneIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import CreateOrEditContactModal from "../CreateOrEditContactModal";

const filter = createFilterOptions({
  trim: true,
});

function telefone_validation(telefone) {
  telefone = telefone.replace(/\D/g, '');
  if (telefone.length < 10 || telefone.length > 11) return false;
  if (telefone.length === 11 && telefone[2] !== '9') return false;
  for (let n = 0; n < 10; n++) {
    if (telefone === String(n).repeat(11) || telefone === String(n).repeat(12)) return false;
  }
  const codigosDDD = [
    11, 12, 13, 14, 15, 16, 17, 18, 19,
    21, 22, 24, 27, 28, 31, 32, 33, 34,
    35, 37, 38, 41, 42, 43, 44, 45, 46,
    47, 48, 49, 51, 53, 54, 55, 61, 62,
    64, 63, 65, 66, 67, 68, 69, 71, 73,
    74, 75, 77, 79, 81, 82, 83, 84, 85,
    86, 87, 88, 89, 91, 92, 93, 94, 95,
    96, 97, 98, 99
  ];
  if (!codigosDDD.includes(parseInt(telefone.substring(0, 2)))) return false;
  return true;
}

const CreateTicketModal = ({ open, onClose, initialContact = {} }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [phone, setPhone] = useState('');

  const handleChange = (event) => {
    let formattedPhone = event.target.value.replace(/\D/g, '');
    if (formattedPhone.length <= 10) {
      formattedPhone = formattedPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      formattedPhone = formattedPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (formattedPhone.length > 15) return;
    setPhone(formattedPhone);
  };

  useEffect(() => {
    if (initialContact.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    if (open) {
      fetchWhatsappConnections();
    }
  }, [open]);

  useEffect(() => {
    setSelectedContact(null);
    setPhone('');
    setSearchParam('');
    setOptions([]);
  }, [tabValue]);

  const fetchWhatsappConnections = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsappConnections(data);
    } catch (err) {
      ToastError(err);
    }
  };

  useEffect(() => {
    if (!open || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("listContacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          ToastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedContact(null);
    setPhone('');
    setSelectedQueue("");
    setWhatsappId("");
    setTabValue(0);
    setOptions([]);
    setNewContact({});
  };

  const handleSaveTicket = async contactId => {
    if (!contactId) return;
    if (!whatsappId) {
      ToastError("Selecione uma conexão");
      return;
    }
    if (selectedQueue === "" && user.profile !== 'admin') {
      ToastError("Selecione um setor");
      return;
    }
    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });
      onClose(ticket);
    } catch (err) {
      ToastError(err);
    }
    setLoading(false);
  };

  const handleSaveTicketNewNumber = async e => {
    if (!phone) return;
    if (!whatsappId) {
      ToastError("Selecione uma conexão");
      return;
    }
    if (!telefone_validation(phone)) {
      ToastError("Número inválido, tente novamente.");
      return;
    }
    if (selectedQueue === "" && user.profile !== 'admin') {
      ToastError("Selecione um setor");
      return;
    }
    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: null,
        queueId,
        whatsappId,
        number: phone,
        userId: user.id,
        status: "open",
      });
      onClose(ticket);
    } catch (err) {
      ToastError(err);
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    handleSaveTicket(contact.id);
  };

  const createAddContactOption = (options, params) => {
    const filtered = filter(options, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = (props, option) => {
    if (option.number) {
      return (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.number}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box component="li" {...props}>
          <PersonAddIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">
            {i18n.t("newTicketModal.add")} "{option.name}"
          </Typography>
        </Box>
      );
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const isFormValid = () => {
    if (!whatsappId) return false;

    if (tabValue === 0) {
      return selectedContact && selectedContact.id;
    } else {
      return phone.length >= 14 && telefone_validation(phone);
    }
  };

  return (
    <>
      <CreateOrEditContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
        onSave={handleAddNewContactTicket}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              {i18n.t("newTicketModal.title")}
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>

            {/* Conexão WhatsApp */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Conexão WhatsApp</InputLabel>
              <Select
                value={whatsappId}
                onChange={(e) => setWhatsappId(e.target.value)}
                label="Conexão WhatsApp"
              >
                {whatsappConnections.map((connection) => (
                  <MenuItem key={connection.id} value={connection.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: connection.status === 'CONNECTED' ? 'success.main' : 'error.main'
                        }}
                      />
                      <Typography variant="body2">
                        {connection.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tabs para Buscar/Criar */}
            <Box>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                variant="fullWidth"
              >
                <Tab
                  icon={<SearchIcon />}
                  iconPosition="start"
                  label="Buscar Contato"
                />
                <Tab
                  icon={<PhoneIcon />}
                  iconPosition="start"
                  label="Novo Número"
                />
              </Tabs>
            </Box>

            {/* Conteúdo das Tabs */}
            {tabValue === 0 && initialContact.id === undefined ? (
              <Box>
                <Autocomplete
                  options={options}
                  loading={loading}
                  onChange={(e, newValue) => handleSelectOption(e, newValue)}
                  filterOptions={createAddContactOption}
                  getOptionLabel={renderOptionLabel}
                  renderOption={renderOption}
                  freeSolo
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Buscar contato"
                      placeholder="Digite pelo menos 3 caracteres"
                      size="small"
                      onChange={e => setSearchParam(e.target.value)}
                      onKeyPress={e => {
                        if (loading || !selectedContact) return;
                        if (e.key === "Enter") {
                          handleSaveTicket(selectedContact.id);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {selectedContact && (
                  <Chip
                    label={`${selectedContact.name} - ${selectedContact.number}`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            ) : tabValue === 1 ? (
              <TextField
                value={phone}
                onChange={handleChange}
                label="Número do WhatsApp"
                size="small"
                fullWidth
                placeholder="(11) 99999-9999"
                error={phone.length > 0 && !telefone_validation(phone)}
                helperText={phone && !telefone_validation(phone) ? "Número inválido" : "Digite o número com DDD"}
              />
            ) : null}

            {/* Setor */}
            <FormControl fullWidth size="small">
              <InputLabel>Setor</InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label="Setor"
              >
                {user.queues?.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          backgroundColor: queue.color || 'primary.main'
                        }}
                      />
                      <Typography variant="body2">
                        {queue.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {user.profile === 'admin' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Opcional para administradores
                </Typography>
              )}
            </FormControl>

          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <ButtonWithSpinner
            variant="contained"
            disabled={!isFormValid()}
            onClick={() => {
              tabValue === 0
                ? handleSaveTicket(selectedContact.id)
                : handleSaveTicketNewNumber(phone)
            }}
            loading={loading}
          >
            Criar Atendimento
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateTicketModal;